import React, { useState, useEffect, useRef } from "react";
import { DollarSign, Wallet, Database, User, Shield, PlusCircle, MinusCircle, Key, Trash2 } from 'lucide-react';

function UserManagement({ darkMode }) {
  const [users, setUsers] = useState([]);
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [quantityType, setQuantityType] = useState(null); // 用於保存用戶和帳戶類型
  const [quantity, setQuantity] = useState(""); // 用戶輸入的數量
	// 在現有的 state 聲明中添加
	const [accountModalType, setAccountModalType] = useState(null); // 'add' 或 'decrease'
	const [selectedAccountType, setSelectedAccountType] = useState(null); // '60' 或 '100'
	// 添加新的狀態
const [userQuantities, setUserQuantities] = useState({});
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const socketRef = useRef(null); // WebSocket 实例
  let isFetching = false; // 防止重复调用 fetchUsers

  useEffect(() => {
    fetchUsers();
    setupWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  const fetchUsers = async () => {
    if (isFetching) return; // 防止重复调用
    isFetching = true;
    setLoading(true);
    setError("");
    try {
      const response = await fetch("https://api.mcvanuatu.com/api/users");
      if (!response.ok) {
        throw new Error("Failed to fetch users.");
      }
      const data = await response.json();
      setUsers(data);
	  
		// 獲取每個用戶的數量信息
		data.forEach(user => {
		  if (user.role !== "ADMIN") {
			fetchUserQuantity(user.account);
		  }
		});
    } catch (err) {
      setError(err.message);
      console.error("Error fetching users:", err);
    } finally {
      isFetching = false;
      setLoading(false);
    }
  };
  
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const wsUrl = `${wsProtocol}//${window.location.host}/ws`;


	const setupWebSocket = () => {
	  if (socketRef.current) {
		return;
	  }

	  socketRef.current = new WebSocket(wsUrl);


	  socketRef.current.onopen = () => {
		console.log("WebSocket connected");
	  };

	  socketRef.current.onmessage = async (event) => {
		const data = JSON.parse(event.data);
		if (data.type === "USER_UPDATE") {
		  // 更新用戶列表和所有用戶的數量信息
		  await fetchUsers();
		  users.forEach(user => {
			if (user.role !== "ADMIN") {
			  fetchUserQuantity(user.account);
			}
		  });
		}
	  };

	  socketRef.current.onclose = () => {
		console.log("WebSocket disconnected");
		socketRef.current = null;
		setTimeout(setupWebSocket, 1000);
	  };

	  socketRef.current.onerror = (error) => {
		console.error("WebSocket error:", error);
	  };
	};
  
// 添加獲取帳戶數量的函數
const fetchUserQuantity = async (account, retryCount = 0, maxRetries = 10) => {
  try {
    const response = await fetch(`https://api.mcvanuatu.com/api/quantity/${account}`);
    if (!response.ok) {
      throw new Error("Failed to fetch quantity");
    }
    const data = await response.json();
    setUserQuantities(prev => ({
      ...prev,
      [account]: data
    }));
    return true; // 成功獲取數據
  } catch (err) {
    console.error(`Error fetching quantity (attempt ${retryCount + 1}):`, err);
    
    if (retryCount < maxRetries) {
      // 等待3秒後重試
      await new Promise(resolve => setTimeout(resolve, 3000));
      return fetchUserQuantity(account, retryCount + 1, maxRetries);
    } else {
      console.error(`Failed to fetch quantity after ${maxRetries} attempts`);
      return false; // 達到最大重試次數
    }
  }
};

// 创建用户
const createUser = async () => {
if (!account.trim() || !password.trim()) {
setMessage("帳戶名和密碼是必填的！ Account and password are required!");
return;
}

try {
  const response = await fetch("https://api.mcvanuatu.com/api/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ account, password }),
  });

  if (!response.ok) {
    throw new Error("Failed to create user.");
  }

  setMessage("用戶創建成功！ User created successfully!");
  setAccount("");
  setPassword("");
  setShowCreateModal(false);


  await fetchUsers(); // 调用更新用户列表
  
    // 獲取新創建用戶的數量信息
    await fetchUserQuantity(account);

  if (socketRef.current) {
    socketRef.current.send(JSON.stringify({ type: "USER_UPDATE" }));
  }
} catch (err) {
  setMessage(err.message);
  console.error("Error creating user:", err);
}
};


	  // 更新帳戶數量
	 const handleQuantityUpdate = async () => {
	  if (!quantity || isNaN(quantity) || quantity <= 0) {
		setMessage("請輸入有效的數量！ Please enter a valid quantity!");
		return;
	  }

	  try {
		const response = await fetch(
		  `https://api.mcvanuatu.com/api/quantity/${quantityType.user.account}/accounts`,
		  {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
			  accountType: quantityType.accountType,
			  quantity: parseInt(quantity, 10),
			}),
		  }
		);

		if (!response.ok) {
		  throw new Error("Failed to update account quantity.");
		}

		// 更新數量顯示
		await fetchUserQuantity(quantityType.user.account);

		setMessage(
		  `成功增加 ${quantity} 個 $${quantityType.accountType} 帳戶！ Successfully added ${quantity} $${quantityType.accountType} accounts!`
		);
		
		setQuantityType(null);
		setSelectedAccountType(null);
		setQuantity("");
		setAccountModalType(null);

		// 觸發 socket 更新
		if (socketRef.current) {
		  socketRef.current.send(JSON.stringify({ type: "USER_UPDATE" }));
		}
	  } catch (err) {
		setMessage(err.message);
		console.error("Error updating account quantity:", err);
	  }
	};

	const handleQuantityDecrease = async () => {
	  if (!quantity || isNaN(quantity) || quantity <= 0) {
		setMessage("請輸入有效的數量！ Please enter a valid quantity!");
		return;
	  }

	  try {
		const response = await fetch(
		  `https://api.mcvanuatu.com/api/quantity/${quantityType.user.account}/decrease`,
		  {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
			  accountType: quantityType.accountType,
			  quantity: parseInt(quantity, 10),
			}),
		  }
		);

		if (!response.ok) {
		  throw new Error("Failed to decrease account quantity.");
		}

		// 更新數量顯示
		await fetchUserQuantity(quantityType.user.account);

		setMessage(
		  `成功減少 ${quantity} 個 $${quantityType.accountType} 帳戶！ Successfully decreased ${quantity} $${quantityType.accountType} accounts!`
		);
		
		setQuantityType(null);
		setSelectedAccountType(null);
		setQuantity("");
		setAccountModalType(null);

		// 觸發 socket 更新
		if (socketRef.current) {
		  socketRef.current.send(JSON.stringify({ type: "USER_UPDATE" }));
		}
	  } catch (err) {
		setMessage(err.message);
		console.error("Error decreasing account quantity:", err);
	  }
	};

  // 刪除用戶
	  const deleteUser = async () => {
	  if (!userToDelete) return;

	  try {
		const response = await fetch(
		  `https://api.mcvanuatu.com/api/users/${userToDelete.account}`,
		  { method: "DELETE" }
		);

		if (!response.ok) {
		  throw new Error("Failed to delete user.");
		}

		setMessage("用戶刪除成功！ User deleted successfully!");
		setUserToDelete(null);

		// 更新用戶列表
		await fetchUsers();
		// 從 userQuantities 中移除已刪除用戶的數據
		setUserQuantities(prev => {
		  const newQuantities = { ...prev };
		  delete newQuantities[userToDelete.account];
		  return newQuantities;
		});

		// 觸發 socket 更新
		if (socketRef.current) {
		  socketRef.current.send(JSON.stringify({ type: "USER_UPDATE" }));
		}
	  } catch (err) {
		setMessage(err.message);
		console.error("Error deleting user:", err);
	  }
	};
	
  // 修改密碼
  const updatePassword = async () => {
    if (!newPassword.trim()) {
      setMessage("新密碼是必填的！ New password is required!");
      return;
    }

    try {
      const response = await fetch(
        `https://api.mcvanuatu.com/api/users/${selectedUser.account}/password`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: newPassword }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update password.");
      }

      setMessage("密碼修改成功！ Password updated successfully!");
      setSelectedUser(null);
      setNewPassword("");
    } catch (err) {
      setMessage(err.message);
      console.error("Error updating password:", err);
    }
  };

  return (
    <div className="container">
      <h3
        className={`text-xl font-semibold ${
          darkMode ? "text-white" : "text-[#2C2C2C]"
        }`}
      >
        用戶管理 User Management
      </h3>

      {message && (
        <div
          className={`mt-4 p-4 rounded-md shadow-md ${
            darkMode
              ? "bg-[#1E1E1E] text-[#FFFFFF]"
              : "bg-[#f4e6d3] text-[#2C2C2C]"
          }`}
        >
          {message}
        </div>
      )}
	  
	<div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
		<input
		  type="text"
		  placeholder="帳戶名 Account Name"
		  value={account}
		  onChange={(e) => setAccount(e.target.value)}
		  className={`p-2 border rounded-md flex-1 ${
			darkMode ? "bg-[#1E1E1E] text-white border-[#404040] border" : "bg-white text-[#121212] border"
		  }`}
		/>
		<input
		  type="password"
		  placeholder="密碼 Password"
		  value={password}
		  onChange={(e) => setPassword(e.target.value)}
		  className={`p-2 border rounded-md flex-1 ${
			darkMode ? "bg-[#1E1E1E] text-white border-[#404040] border" : "bg-white text-[#121212] border"
		  }`}
		/>
		<button
		  onClick={() => setShowCreateModal(true)} // 打开创建模态框
		  className={`py-2 px-4 rounded-lg font-semibold shadow-md transition ${
			account.trim() && password.trim()
			  ? darkMode
				  ? "bg-gradient-to-r from-[#404040] to-[#2C2C2C] text-white hover:from-[#2C2C2C] hover:to-[#404040]" // 深色：深灰漸變
				  : "bg-gradient-to-r from-[#F0F0F0] to-[#E0E0E0] text-[#2C2C2C] hover:from-[#E0E0E0] hover:to-[#F0F0F0]" // 淺色：淺灰漸變
			  : "bg-gray-400 text-gray-700 cursor-not-allowed"
		  }`}
		  disabled={!account.trim() || !password.trim()} // 按钮禁用逻辑
		>
		  創建 Create
		</button>
	  </div>
	  
		{showCreateModal && (
		  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
			<div
			  className={`p-6 rounded-md shadow-md ${
				darkMode
				  ? "bg-[#1E1E1E] text-[#FFFFFF]"
				  : "bg-white text-[#121212]"
			  }`}
			>
			  <h4 className="text-lg font-bold">確定創建該用戶嗎？</h4>
			  <p className="mt-2 text-sm">用戶名：{account}</p>
			  <p className="text-sm">密碼：{password}</p>
			  <div className="mt-4 flex justify-end space-x-2">
				<button
				  onClick={createUser}
				  className={`py-2 px-4 rounded-lg font-semibold shadow-md transition ${
					darkMode
					  ? "bg-green-600 text-white hover:bg-green-700"
					  : "bg-green-500 text-white hover:bg-green-600"
				  }`}
				>
				  確定 Confirm
				</button>
				<button
				  onClick={() => setShowCreateModal(false)} // 关闭模态框
				  className={`py-2 px-4 rounded-lg font-semibold shadow-md transition ${
					darkMode
					  ? "bg-gray-600 text-white hover:bg-gray-700"
					  : "bg-gray-500 text-white hover:bg-gray-600"
				  }`}
				>
				  取消 Cancel
				</button>
			  </div>
			</div>
		  </div>
		)}

      {loading ? (
        <p className="mt-4 text-blue-500">加載中 Loading...</p>
      ) : error ? (
        <p className="mt-4 text-red-500">錯誤 Error: {error}</p>
      ) : (
<ul className="mt-4 space-y-2">
  {users.map((user) => (
    <li
      key={user.account}
      className={`flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 rounded-lg shadow-md ${
        darkMode ? "bg-[#1E1E1E] text-[#FFFFFF] border-[#404040] border" : "bg-white text-[#121212] border"
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center">
        {/* 用戶名稱區域 */}
        <div className="flex items-center space-x-4">
          {user.role === "ADMIN" ? (
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
          ) : (
            <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
          )}
          <span className="text-sm sm:text-base font-medium">{user.account}</span>
        </div>

        {/* 卡片區域 */}
        {user.role !== "ADMIN" && userQuantities[user.account] && (
          <div className="flex space-x-2 mt-2 sm:mt-0 sm:ml-4">
            {/* $60 帳戶數量卡片 */}
            <div className={`flex-1 sm:flex-initial flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border-1 transition-all duration-200 ${
              darkMode 
                ? "bg-[#121212] border-[#404040] hover:border-[#2C2C2C]" 
                : "bg-[#dbdbdb] border-[#FFFFFF] hover:border-[#404040]"
            }`}>
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <div className={`p-1 sm:p-1.5 rounded-full ${
                  darkMode ? "bg-[#1E1E1E]" : "bg-[#F5F5F5]"
                }`}>
                  <DollarSign className={`w-3 h-3 sm:w-4 sm:h-4 ${
                    darkMode ? "text-[#FFFFFF]" : "text-[#121212]"
                  }`} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] sm:text-xs opacity-75">ALIPAY60</span>
                  <span className="text-sm sm:text-base font-bold">
                    {userQuantities[user.account].discount60 || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* $100 帳戶數量卡片 */}
            <div className={`flex-1 sm:flex-initial flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border-1 transition-all duration-200 ${
              darkMode 
                ? "bg-[#121212] border-[#404040] hover:border-[#2C2C2C]" 
                : "bg-[#dbdbdb] border-[#FFFFFF] hover:border-[#404040]"
            }`}>
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <div className={`p-1 sm:p-1.5 rounded-full ${
                  darkMode ? "bg-[#1E1E1E]" : "bg-[#F5F5F5]"
                }`}>
                  <Wallet className={`w-3 h-3 sm:w-4 sm:h-4 ${
                    darkMode ? "text-[#FFFFFF]" : "text-[#121212]"
                  }`} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] sm:text-xs opacity-75">HKBRNU216</span>
                  <span className="text-sm sm:text-base font-bold">
                    {userQuantities[user.account].discount100 || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 按鈕組 */}
      {user.role !== "ADMIN" && (
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto mt-4 sm:mt-0 sm:ml-4">
          <button
            onClick={() => {
              setQuantityType({ user, accountType: null });
              setAccountModalType('add');
            }}
            className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-md font-semibold shadow-md transition ${
              darkMode
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-green-500 text-white hover:bg-green-600"
            }`}
          >
            <PlusCircle className="w-5 h-5 mr-2" />
            增加 Add
          </button>

          <button
            onClick={() => {
              setQuantityType({ user, accountType: null });
              setAccountModalType('decrease');
            }}
            className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-md font-semibold shadow-md transition ${
              darkMode
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-red-500 text-white hover:bg-red-600"
            }`}
          >
            <MinusCircle className="w-5 h-5 mr-2" />
            減少 Minus
          </button>

          <button
            onClick={() => setSelectedUser(user)}
            className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-md font-semibold shadow-md transition ${
              darkMode
                ? "bg-yellow-600 text-white hover:bg-yellow-700"
                : "bg-yellow-500 text-white hover:bg-yellow-600"
            }`}
          >
            <Key className="w-5 h-5 mr-2" />
            密碼 Password
          </button>

          <button
            onClick={() => setUserToDelete(user)}
            className={`flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-md font-semibold shadow-md transition ${
              darkMode
                ? "bg-red-800 text-white hover:bg-red-900"
                : "bg-red-700 text-white hover:bg-red-800"
            }`}
          >
            <Trash2 className="w-5 h-5 mr-2" />
            刪除 Delete
          </button>
        </div>
      )}
    </li>
  ))}
</ul>
      )}
	  

      {/* 確定添加數量模態框 */}
		{quantityType && (
		  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
			<div
			  className={`p-6 rounded-lg shadow-lg w-96 ${
				darkMode
				  ? "bg-[#1E1E1E] text-[#FFFFFF]"
				  : "bg-white text-[#121212]"
			  }`}
			>
			  <h4 className="text-xl font-bold mb-4">
				{accountModalType === 'add' ? '增加帳戶數量 Add Account' : '減少帳戶數量 Minus Account'}
			  </h4>
			  <p className="text-sm mb-4">
				用戶名 Account：<span className="font-semibold">{quantityType.user.account}</span>
			  </p>
			  
			  {/* 改進的帳戶類型選擇 */}
			  <div className="mb-4">
				<label className="block text-sm font-medium mb-2">帳戶類型 Account Type</label>
				<div className="grid grid-cols-2 gap-3">
				  <button
					onClick={() => {
					  setSelectedAccountType('60');
					  setQuantityType(prev => ({...prev, accountType: '60'}));
					}}
					className={`p-3 rounded-lg border-2 transition-all duration-200 ${
					  selectedAccountType === '60'
    ? darkMode
      ? 'border-white bg-white text-[#1E1E1E]' // 選擇後：白底黑字
      : 'border-[#2C2C2C] bg-[#2C2C2C] text-white' // 淺色模式：黑底白字
    : darkMode
      ? 'border-[#707070] text-[#D0D0D0] hover:border-white hover:text-white' // 未選中：灰邊框+灰文字
      : 'border-[#E0E0E0] text-[#404040] hover:border-[#2C2C2C] hover:text-[#2C2C2C]'
					}`}
				  >
					<span className="block text-lg font-bold">$60</span>
					<span className="text-xs">ALIPAY60 優惠卷</span>
				  </button>
				  
				  <button
					onClick={() => {
					  setSelectedAccountType('100');
					  setQuantityType(prev => ({...prev, accountType: '100'}));
					}}
					className={`p-3 rounded-lg border-2 transition-all duration-200 ${
					  selectedAccountType === '100'
    ? darkMode
      ? 'border-white bg-white text-[#1E1E1E]' // 選擇後：白底黑字
      : 'border-[#2C2C2C] bg-[#2C2C2C] text-white' // 淺色模式：黑底白字
    : darkMode
      ? 'border-[#707070] text-[#D0D0D0] hover:border-white hover:text-white' // 未選中：灰邊框+灰文字
      : 'border-[#E0E0E0] text-[#404040] hover:border-[#2C2C2C] hover:text-[#2C2C2C]'
					}`}
				  >
					<span className="block text-lg font-bold">$100</span>
					<span className="text-xs">HKBRNU216 優惠卷</span>
				  </button>
				</div>
			  </div>

			  <div className="mb-4">
				<label className="block text-sm font-medium mb-2">
				  數量 Quantity
				</label>
				<input
				  type="number"
				  placeholder="請輸入數量 Enter quantity"
				  value={quantity}
				  onChange={(e) => setQuantity(e.target.value)}
				  className={`w-full p-3 border rounded-lg transition-all duration-200 ${
					darkMode
					  ? "bg-[#121212] text-[#FFFFFF] border-[#1E1E1E] focus:border-[#2C2C2C]"
					  : "bg-white text-[#121212] border-[#E0E0E0] focus:border-[#2C2C2C]"
				  }`}
				/>
			  </div>

			  <div className="flex justify-end space-x-3">
				<button
				  onClick={async () => {
					if (accountModalType === 'add') {
					  await handleQuantityUpdate();
					} else {
					  await handleQuantityDecrease();
					}
					setSelectedAccountType(null);  // 清空選擇
					setQuantity('');  // 清空數量
				  }}
				  disabled={!selectedAccountType || !quantity}
				  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
					!selectedAccountType || !quantity
					  ? "bg-gray-400 text-gray-700 cursor-not-allowed"
					  : darkMode
						  ? "bg-green-700 text-white hover:bg-green-600" 
						  : "bg-green-500 text-white hover:bg-green-400"
				  }`}
				>
				  確定 Confirm
				</button>
				<button
				  onClick={() => {
					setQuantityType(null);
					setSelectedAccountType(null);
					setQuantity('');
					setAccountModalType(null);
				  }}
				  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
					darkMode
					  ? "bg-red-700 text-white hover:bg-red-600"
					  : "bg-red-500 text-white hover:bg-red-400"
				  }`}
				>
				  取消 Cancel
				</button>
			  </div>
			</div>
		  </div>
		)}

      {/* 修改密碼模態框 */}
      {selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            className={`p-6 rounded-md shadow-md ${
              darkMode
                ? "bg-[#1E1E1E] text-[#FFFFFF]"
                : "bg-white text-[#121212]"
            }`}
          >
            <h4 className="text-lg font-bold">修改該用戶密碼 Change Password</h4>
            <p className="mt-2 text-sm">用戶名 Account：{selectedUser.account}</p>
            <input
              type="password"
              placeholder="新密碼 New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={`mt-2 p-2 border rounded-md w-full ${
                darkMode
                  ? "bg-[#121212] text-[#FFFFFF]"
                  : "bg-white text-[#121212]"
              }`}
            />
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={updatePassword}
                className={`py-2 px-4 rounded-lg font-semibold shadow-md transition ${
                  darkMode
                    ? "bg-yellow-600 text-white hover:bg-yellow-700"
                    : "bg-yellow-500 text-white hover:bg-yellow-600"
                }`}
              >
                確定 Confirm
              </button>
              <button
                onClick={() => setSelectedUser(null)}
                className={`py-2 px-4 rounded-lg font-semibold shadow-md transition ${
                  darkMode
                    ? "bg-gray-600 text-white hover:bg-gray-700"
                    : "bg-gray-500 text-white hover:bg-gray-600"
                }`}
              >
                取消 Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 刪除用戶模態框 */}
      {userToDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            className={`p-6 rounded-md shadow-md ${
              darkMode
                ? "bg-[#1E1E1E] text-[#FFFFFF]"
                : "bg-white text-[#121212]"
            }`}
          >
            <h4 className="text-lg font-bold">刪除該用戶 Delete User</h4>
            <p className="mt-2 text-sm">用戶名 Account：{userToDelete.account}</p>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={deleteUser}
                className={`py-2 px-4 rounded-lg font-semibold shadow-md transition ${
                  darkMode
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-red-500 text-white hover:bg-red-600"
                }`}
              >
                確定 Confirm
              </button>
              <button
                onClick={() => setUserToDelete(null)}
                className={`py-2 px-4 rounded-lg font-semibold shadow-md transition ${
                  darkMode
                    ? "bg-gray-600 text-white hover:bg-gray-700"
                    : "bg-gray-500 text-white hover:bg-gray-600"
                }`}
              >
                取消 Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;